import React from "react";
import PanelShell from "../shared/PanelShell";
import { SkeletonChart } from "../shared/Skeleton";
import { markScale, rowMarks, rowGaps, largestGap } from "./panels/drainMarks";
import DrainStage from "./DrainStage";
import DrainLoss from "./DrainLoss";

// THE DRAIN — the signature element of both pages (V6 Part F). Replaces the
// left-aligned bar funnel entirely.
//
// "A bar chart of six numbers communicates six numbers. This must communicate that
// 128 people called and 128 people left" (F.1). One row per stage, one 6px mark per
// conversation, a constant-length row on which the lit region shrinks down the page.
// The mark arithmetic lives in panels/drainMarks.js (a presentation detail — nothing
// here derives a metric; `count` and `dropped_here` render exactly as the RPC gives
// them). This container composes DrainStage rows with DrainLoss in the gaps.
//
// THE LARGEST DROP IS THE BACKEND'S DECISION, NEVER OURS. Exactly one stage carries
// `is_largest_drop: true`; that gap gets the leaking treatment at greater weight
// (F.4). We read the flag — we never hardcode which stage, and never re-derive "the
// biggest one" when the backend has already said so. The computed `largestGap` is a
// fallback used only if no stage carries the flag at all.
//
// INTERACTION (F.4): clicking a stage does three things at once — highlights the
// stage (activeStage), filters the rail to stages[].conversation_ids, and opens the
// DrillOver. TheDrain owns only the highlight + the callback; the page wires the
// filter and the drill and shows the RailFilterChip.
//
// DEGENERATE (F.5): zero conversations → the panel does not render at all and the
// page opens on the money strip. `hasData` is false when there are no stages, and
// PanelShell renders nothing meaningful — the AnalysisColumn omits the panel entirely
// in that case (it passes no stages).

const stageHandle = (stage, i) =>
  stage?.key
  || (stage?.label ? String(stage.label).toLowerCase().replace(/[^a-z0-9]+/g, "-") : `stage-${i}`);

const TheDrain = ({
  panelKey = "voice_funnel",
  label = "Where people went",
  say,
  stages,
  loading = false,
  onStageClick,
  activeStage = null,
  // Defaults to a full-width card (the spec's funnel), but the AnalysisColumn pairs
  // it 50/50 with Barriers and passes fullWidth={false} so it honours the grid.
  fullWidth = true,
}) => {
  const list = Array.isArray(stages) ? stages : [];
  // The widest row — normally the first stage, taken as a max so a non-monotonic
  // payload cannot draw a row longer than its own container.
  const total = list.reduce((m, s) => Math.max(m, Number(s?.count ?? 0)), 0);
  const scale = markScale(total);
  const gaps = rowGaps(list);

  // The gap that leaks: the stage the backend flagged, and only if none is flagged
  // do we fall back to the computed maximum. This is the "never hardcode which stage"
  // rule made literal.
  const flagged = list.find((s) => s?.is_largest_drop === true);
  const leakingKey = flagged ? flagged.key : (largestGap(gaps)?.key ?? null);

  return (
    <PanelShell
      panelKey={panelKey}
      label={label}
      say={say}
      hasData={list.length > 0}
      loading={loading}
      fullWidth={fullWidth}
    >
      {loading && list.length === 0 ? (
        <SkeletonChart height={260} />
      ) : (
        <div data-testid="drain">
          {list.map((stage, i) => {
            const handle = stageHandle(stage, i);
            const gap = gaps.find((g) => g.afterIndex === i);
            const isActive =
              activeStage != null && (activeStage === stage.key || activeStage === handle);
            return (
              <React.Fragment key={stage.key || i}>
                <DrainStage
                  stage={stage}
                  handle={handle}
                  marks={rowMarks(stage.count, total, scale)}
                  onStageClick={onStageClick}
                  isActive={isActive}
                />
                {gap && (
                  <DrainLoss
                    dropped={gap.dropped}
                    label={gap.label}
                    leaking={leakingKey != null && gap.key === leakingKey}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </PanelShell>
  );
};

export default TheDrain;
